import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Textarea,
} from '@fluentui/react-components';
import { submitRuleSuggestion } from '../api';

const MAX_LENGTH = 2000;

/** Lets a reviewer propose a new QC rule to the template owner — shown in the admin's Suggestions
 * tab for the owner to approve (creates a real rule) or reject. */
export function SuggestRuleDialog({ open, onClose, templateId }: { open: boolean; onClose: () => void; templateId: string }) {
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  function handleClose() {
    setText('');
    setError(null);
    setSubmitted(false);
    onClose();
  }

  async function handleSubmit() {
    const suggestionText = text.trim();
    if (!suggestionText) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitRuleSuggestion(templateId, suggestionText);
      setSubmitted(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to submit suggestion');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && handleClose()}>
      <DialogSurface style={{ margin: 16 }}>
        <DialogBody>
          <DialogTitle>Suggest a new QC rule</DialogTitle>
          <DialogContent>
            {submitted ? (
              <div style={{ fontSize: 13, color: '#2e7d32' }}>Thanks — your suggestion was sent to the template owner for review.</div>
            ) : (
              <>
                <div style={{ fontSize: 13, color: '#444', marginBottom: 8 }}>
                  Describe the check you think should be added to this template. The owner can approve it as a new rule.
                </div>
                <Textarea
                  style={{ width: '100%' }}
                  rows={5}
                  maxLength={MAX_LENGTH}
                  value={text}
                  onChange={(_, data) => setText(data.value)}
                  placeholder='e.g. "Verify every chart cites its data source"'
                />
                <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                  {text.length}/{MAX_LENGTH}
                </div>
                {error && <div style={{ color: '#b91c1c', fontSize: 13, marginTop: 4 }}>{error}</div>}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={handleClose}>
              {submitted ? 'Close' : 'Cancel'}
            </Button>
            {!submitted && (
              <Button appearance="primary" disabled={!text.trim() || submitting} onClick={() => void handleSubmit()}>
                Submit
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
